import axios, { AxiosError } from 'axios';

/**
 * 对外收集表单的 HTTP 客户端。
 *
 * 🔴 与主站的 client 有两个关键区别：
 *
 * 1. **没有 Authorization 拦截器，也没有 401 跳登录。**
 *    填写人不是本系统的用户，没有账号也不会有。身份完全由 URL 里的 token + 可选的
 *    短期 collectToken 表达，两者都作为请求参数显式传递。
 *    任何「自动带上某个全局凭据」的逻辑都不该出现在这个项目里。
 *
 * 2. **错误文案在这里本地化成英文，不直接透传服务端的 message。**
 *    服务端的 message 是写给站内中文用户看的，而这个页面的读者是境外收款人。
 *    见 ERROR_MESSAGES 与 apiErrorMessage。
 */
export function apiBase(): string {
  return import.meta.env.VITE_API_BASE_URL || '/api';
}

export const api = axios.create({ baseURL: apiBase() });

interface ApiErrorBody {
  error?: {
    code?: string;
    message?: string;
    fields?: string[];
    /** 429 时服务端给的建议等待秒数。 */
    retryAfter?: number;
    /** BAD_CODE 的细分原因，见后端 verifyCollectCode。 */
    reason?: 'expired' | 'mismatch';
  };
}

function body(err: unknown): ApiErrorBody['error'] | undefined {
  return (err as AxiosError<ApiErrorBody>)?.response?.data?.error;
}

export function apiErrorCode(err: unknown): string | undefined {
  return body(err)?.code;
}

/** Airwallex 校验失败时的出错字段 path，用来把红字标到具体那一格。 */
export function apiErrorFields(err: unknown): string[] {
  return body(err)?.fields ?? [];
}

const GENERIC = 'Something went wrong. Please try again.';
/** 组织自己的 Airwallex 配置有问题 —— 填写人做什么都没用，只能去找发链接的人。 */
const NOT_ACCEPTING =
  "This link can't accept submissions right now. Please contact the person who sent it to you.";
const UPSTREAM = "We couldn't reach the banking network. Please try again in a moment.";
/** 请求形状被拒。填写人改不了，刷新重来是唯一有意义的建议。 */
const BAD_REQUEST = "That request wasn't accepted. Please refresh the page and try again.";

/**
 * 服务端错误短码 → 给填写人看的英文。
 *
 * 🔴 按**短码**而不是按 message 分支：message 是中文的、会被改写，短码是对外契约。
 * 新增后端错误码时如果忘了在这里补一行，用户会看到 GENERIC —— 不好但不会泄漏中文，
 * 这是刻意选的失败方向。
 */
const ERROR_MESSAGES: Record<string, string> = {
  LINK_UNAVAILABLE: 'This link is invalid or has expired.',
  LINK_ALREADY_FILLED: 'This link has already been completed. There is no need to submit again.',
  VERIFICATION_NOT_REQUIRED: "This link doesn't require email verification.",
  VERIFICATION_REQUIRED: 'Your verification has expired. Please verify your email again.',
  CODE_RATE_LIMITED: 'Too many codes requested. Please try again later.',
  MAIL_FAILED: "We couldn't send the email. Please try again in a moment.",

  /*
   * 🔴 「这个组合不存在」和「这一格填错了」对填写人是两件事。
   *
   * Airwallex 对不存在的组合回 "Provided parameters do not match any schema definition."
   * —— 语法是英文，但看不出该改哪儿，而且它的 details.errors 是空的，连字段都指不出来。
   * 这句话要告诉他下一步做什么：换一个组合。
   */
  PAYEE_COMBINATION_UNSUPPORTED:
    "That combination of country, currency, transfer method and account type isn't supported. Please try a different one.",

  AIRWALLEX_TIMEOUT: UPSTREAM,
  AIRWALLEX_UNREACHABLE: UPSTREAM,
  AIRWALLEX_ERROR: UPSTREAM,
  AIRWALLEX_NOT_FOUND: UPSTREAM,
  AIRWALLEX_AUTH_FAILED: NOT_ACCEPTING,
  AIRWALLEX_NOT_CONFIGURED: NOT_ACCEPTING,

  BAD_REQUEST,
  BAD_TOKEN: BAD_REQUEST,
  BAD_SCHEMA_QUERY: BAD_REQUEST,
  BAD_PAYEE_INPUT: BAD_REQUEST,
  NOT_FOUND: BAD_REQUEST,
  INTERNAL: GENERIC,
};

/** 中日韩统一表意文字。用来兜住「某条后端文案还没被本地化」的情况。 */
const CJK = /[㐀-鿿]/;

export function apiErrorMessage(err: unknown, fallback = GENERIC): string {
  const e = body(err);
  if (!e) return fallback;

  switch (e.code) {
    case 'BAD_CODE':
      // 「没有有效码」和「码不对」对填写人的下一步动作完全不同，后端刻意区分（见 code.ts）
      if (e.reason === 'expired') return 'That code has expired. Request a new one.';
      if (e.reason === 'mismatch') return "That code isn't correct. Please check your email.";
      return "That code isn't valid.";

    case 'CODE_TOO_SOON':
    case 'TOO_MANY_REQUESTS': {
      const wait = e.retryAfter;
      return wait && wait > 0
        ? `Too many attempts. Please try again in ${wait}s.`
        : 'Too many attempts. Please try again in a moment.';
    }
  }

  const mapped = e.code ? ERROR_MESSAGES[e.code] : undefined;
  if (mapped) return mapped;

  // Airwallex 的校验原文是英文且指名道姓（"beneficiary.bank_details.iban is invalid"），
  // 比任何笼统的话都有用，所以透传 —— 但只在确认不含中文时，免得漏一句中文到页面上。
  if (e.message && !CJK.test(e.message)) return e.message;
  return fallback;
}
